import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Chip } from "@mui/material";
import CsvImportDialog from "../../components/csv-import-dialog/CsvImportDialog";
import NewItemsPanel from "../../components/csv-import-dialog/NewItemsPanel";
import {
  setChoice,
  mapToFields,
  describeMappings,
} from "../../components/csv-import-dialog/mappingHelpers";
import { baseAxios } from "../../utils/config";
import BrandManufacturersPanel from "./BrandManufacturersPanel";
import ProductImagesStep from "./ProductImagesStep";

const REQUIRED_COLUMNS = ["barcode", "brand", "salon_cost"];

const FORMAT_GUIDE = [
  "A Meevo product export can be uploaded exactly as downloaded: its columns are recognised automatically",
  "product_name is required unless the product is retail. is_retail: 1 for retail items, 0 or blank for professional products; msrp is required for retail",
  "salon_cost and msrp: numbers, up to 6 decimals (rounded to 2). size_oz is optional; products without a size show under Missing info",
  "brand and category are names. New ones are created on import, or in the preview you can use an existing one or leave the products without",
  "manufacturer is optional. Each brand sits under one manufacturer, decided from the file and shown in the preview",
  "reseller (Meevo: distributor): new names are created, or mapped or left out in the preview",
  "Barcodes must be unique. Products whose barcode already exists are skipped",
  "Pictures are added in a separate step after the import",
];

/** A preview cell showing a value with a "new" chip when `flagKey` is set. */
function withNewChip(valueKey, flagKey) {
  return function NewChipCell(row) {
    const value = row[valueKey];
    if (!value) return "—";
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        {value}
        {row[flagKey] && (
          <Chip label="new" size="small" color="warning" variant="outlined" />
        )}
      </Box>
    );
  };
}

const PREVIEW_COLUMNS = [
  { key: "barcode", label: "Barcode" },
  { key: "product_name", label: "Name" },
  { key: "brand", label: "Brand", render: withNewChip("brand", "brand_is_new") },
  {
    key: "manufacturer",
    label: "Manufacturer",
    render: withNewChip("manufacturer", "manufacturer_is_new"),
  },
  {
    key: "category",
    label: "Category",
    render: withNewChip("category", "category_is_new"),
  },
  {
    key: "reseller",
    label: "Reseller",
    render: withNewChip("reseller", "reseller_is_new"),
  },
  {
    key: "is_retail",
    label: "Retail",
    render: (row) => (row.is_retail ? "Yes" : "—"),
  },
];

const EXTRA_RESULT_COUNTS = [
  { key: "manufacturers_created", label: "Manufacturers created" },
  { key: "brands_created", label: "Brands created" },
  { key: "categories_created", label: "Categories created" },
  { key: "resellers_created", label: "Resellers created" },
];

const EMPTY_MAPS = {
  manufacturer: {},
  brand: {},
  brandManufacturer: {},
  category: {},
  reseller: {},
};

function buildFields(maps) {
  return {
    ...mapToFields("manufacturer_map", maps.manufacturer),
    ...mapToFields("brand_map", maps.brand),
    ...mapToFields("brand_manufacturer_map", maps.brandManufacturer),
    ...mapToFields("category_map", maps.category),
    ...mapToFields("reseller_map", maps.reseller),
  };
}

/**
 * Products CSV import, professional and retail, our template or a raw Meevo
 * export. Flow and layout live in the shared CsvImportDialog; this adds the
 * preview panels for new brands (and their manufacturers), manufacturers,
 * categories and resellers, and the optional Add images step.
 */
export default function ImportProductsDialog({
  open,
  onClose,
  onImportComplete,
}) {
  const [maps, setMaps] = useState(EMPTY_MAPS);
  const [manufacturers, setManufacturers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [resellers, setResellers] = useState([]);

  // Fresh choices and current lists every time the dialog opens.
  useEffect(() => {
    if (!open) return undefined;
    setMaps(EMPTY_MAPS);
    let cancelled = false;
    Promise.all([
      baseAxios.get("/products/manufacturers/all"),
      baseAxios.get("/products/brands/all"),
      baseAxios.get("/resellers", { params: { is_active: "true" } }),
    ])
      .then(([manufacturersRes, brandsRes, resellersRes]) => {
        if (cancelled) return;
        setManufacturers(manufacturersRes.data ?? []);
        setBrands(brandsRes.data ?? []);
        const list = Array.isArray(resellersRes.data)
          ? resellersRes.data
          : (resellersRes.data?.resellers ?? []);
        setResellers(list);
      })
      .catch(() => {
        if (cancelled) return;
        setManufacturers([]);
        setBrands([]);
        setResellers([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const manufacturerOptions = useMemo(
    () =>
      manufacturers.map((m) => ({
        id: m.manufacturer_id,
        label: String(m.manufacturer_name || "").trim(),
      })),
    [manufacturers]
  );

  const brandOptions = useMemo(() => {
    const manufacturerNames = new Map(
      manufacturerOptions.map((m) => [m.id, m.label])
    );
    return brands.map((b) => {
      const manufacturerName = manufacturerNames.get(b.manufacturer_id);
      return {
        id: b.brand_id,
        label: manufacturerName
          ? `${b.brand_name} (${manufacturerName})`
          : b.brand_name,
      };
    });
  }, [brands, manufacturerOptions]);

  const resellerOptions = useMemo(
    () =>
      resellers.map((r) => ({
        id: r.reseller_id,
        label: r.reseller_company_name,
      })),
    [resellers]
  );

  const extraFields = useMemo(() => buildFields(maps), [maps]);

  const renderPreviewExtras = useCallback(
    ({ dryRunResult, revalidate, loading }) => {
      // Pass the next fields explicitly to revalidate: this component's state
      // update has not reached the dialog's props yet.
      const choose = (which) => (name, value) => {
        const next = { ...maps, [which]: setChoice(maps[which], name, value) };
        setMaps(next);
        revalidate(buildFields(next));
      };
      const chooseBrandManufacturer = (brandName, manufacturerName) => {
        const next = {
          ...maps,
          brandManufacturer: {
            ...maps.brandManufacturer,
            [brandName]: manufacturerName,
          },
        };
        setMaps(next);
        revalidate(buildFields(next));
      };

      const withoutBrand = dryRunResult?.without_brand ?? 0;

      return (
        <>
          <NewItemsPanel
            title="New brands"
            noun="brand"
            intro="These brands are not in your system yet. They will be created when you import. You can use one of your existing brands instead, or leave the products without a brand (they will then show under Missing info)."
            items={dryRunResult?.new_brands ?? []}
            mappings={describeMappings(
              maps.brand,
              brandOptions,
              "left without a brand"
            )}
            options={brandOptions}
            skipLabel="Leave without a brand"
            disabled={loading}
            onChoose={choose("brand")}
          />
          <BrandManufacturersPanel
            newBrands={dryRunResult?.new_brands ?? []}
            manufacturerOptions={manufacturerOptions}
            disabled={loading}
            onChoose={chooseBrandManufacturer}
          />
          <NewItemsPanel
            title="New manufacturers"
            noun="manufacturer"
            intro="These manufacturers are not in your system yet. They will be created when you import, or you can use one of your existing manufacturers instead."
            items={dryRunResult?.new_manufacturers ?? []}
            mappings={describeMappings(maps.manufacturer, manufacturerOptions)}
            options={manufacturerOptions}
            disabled={loading}
            onChoose={choose("manufacturer")}
          />
          <NewItemsPanel
            title="New categories"
            noun="category"
            intro="These categories will be created under their brand when you import, or you can leave the products without a category."
            items={(dryRunResult?.new_categories ?? []).map((c) => ({
              ...c,
              detail: `under ${c.brand}`,
            }))}
            mappings={describeMappings(
              maps.category,
              [],
              "left without a category"
            )}
            skipLabel="Leave without a category"
            disabled={loading}
            onChoose={choose("category")}
          />
          <NewItemsPanel
            title="New resellers"
            noun="reseller"
            intro="These resellers (distributors) are not in your system yet. They will be created when you import, or you can use one of your existing resellers, or leave the products without one."
            items={dryRunResult?.new_resellers ?? []}
            mappings={describeMappings(
              maps.reseller,
              resellerOptions,
              "left without a reseller"
            )}
            options={resellerOptions}
            skipLabel="Leave without a reseller"
            disabled={loading}
            onChoose={choose("reseller")}
          />
          {withoutBrand > 0 && (
            <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
              {withoutBrand} {withoutBrand === 1 ? "product" : "products"} will be
              imported without a brand or manufacturer and will appear under
              Missing info on Manage Products.
            </Alert>
          )}
        </>
      );
    },
    [maps, brandOptions, manufacturerOptions, resellerOptions]
  );

  const renderAfterResults = useCallback(
    ({ importResult }) => (
      <ProductImagesStep
        products={importResult.inserted_products ?? []}
        description={({ withImage, total }) =>
          `Optional. ${withImage} of ${total} products have an image. ` +
          "Products without one can also be edited later from Manage Products."
        }
      />
    ),
    []
  );

  return (
    <CsvImportDialog
      open={open}
      onClose={onClose}
      onImportComplete={onImportComplete}
      entityPlural="products"
      entityTitle="Products"
      subtitle="Upload our template or a Meevo product export to add products in bulk."
      importPath="/products/import"
      templateFilename="product-import-template.csv"
      requiredColumns={REQUIRED_COLUMNS}
      optionalColumnsHint="Optional: product_name (required unless retail), manufacturer, category, size_oz, is_retail, msrp (required for retail), reseller. Column names are case-insensitive. A Meevo export's own column names are accepted too."
      formatGuide={FORMAT_GUIDE}
      previewColumns={PREVIEW_COLUMNS}
      existingIdKey="existing_product_id"
      extraResultCounts={EXTRA_RESULT_COUNTS}
      extraFields={extraFields}
      renderPreviewExtras={renderPreviewExtras}
      renderAfterResults={renderAfterResults}
      afterResultsLabel="Add images"
    />
  );
}
